import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPinIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { API_BASE_URL, getAuthHeaders } from '../../config/api';
import UniversityVerifiedBadge from './UniversityVerifiedBadge';

const UniversityCard = ({ university, isFromFeaturedSection = false }) => {
  const isPartnerUniversity = isFromFeaturedSection || university.is_partner === true;

  const { isAuthenticated, isStudent, token } = useAuth();
  const navigate = useNavigate();

  const [hasApplied, setHasApplied] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [logoError, setLogoError] = useState(false);

  // Map field names
  const logo = university.profile_picture || university.logo;
  const name = university.university_name || university.name || 'University';
  const location = university.location || university.city || university.address;
  const established = university.established_year || university.established;
  const universityType = university.university_type || university.type;
  const programCount = university.programs_count || (Array.isArray(university.programs) ? university.programs.length : 0);

  const getInitials = (str) => {
    if (!str) return 'UN';
    return str
      .split(' ')
      .filter(w => w.length > 0)
      .slice(0, 2)
      .map(w => w[0].toUpperCase())
      .join('');
  };

  // Check application status on mount for authenticated students
  useEffect(() => {
    if (isAuthenticated && isStudent && isPartnerUniversity && university.id) {
      checkApplicationStatus();
    }
  }, [isAuthenticated, isStudent, isPartnerUniversity, university.id, token]);

  const checkApplicationStatus = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/application/check-status?university_id=${university.id}`,
        {
          method: 'GET',
          headers: getAuthHeaders(token),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setHasApplied(data.has_applied || false);
        setApplicationStatus(data.status || null);
      }
    } catch (error) {
      console.error('Error checking application status:', error);
    }
  };

  const handleCardClick = () => {
    navigate(`/university/${university.slug || university.id}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className="group w-full bg-white rounded-2xl border border-slate-200 hover:border-slate-400 transition-colors duration-200 p-5 sm:p-6 shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-5 cursor-pointer"
    >
      {/* Left Column: Logo & Institutional Info */}
      <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0 w-full">
        {logo && !logoError ? (
          <img
            src={logo}
            alt={`${name} logo`}
            width="64"
            height="64"
            className="w-11 h-11 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl border border-slate-100 object-cover flex-shrink-0 bg-white shadow-2xs p-1"
            onError={() => setLogoError(true)}
          />
        ) : (
          <div className="w-11 h-11 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl border border-slate-200 bg-slate-50 text-slate-700 font-bold text-sm sm:text-lg flex items-center justify-center flex-shrink-0 shadow-2xs">
            {getInitials(name)}
          </div>
        )}

        <div className="min-w-0 flex-1">
          {/* University Name & Verified Badge */}
          <h3 className="text-base sm:text-lg font-bold text-slate-850 leading-snug mb-1.5 break-words">
            <span>{name}</span>
            <span className="inline-flex items-center ml-1.5 align-middle shrink-0">
              <UniversityVerifiedBadge isPartner={isPartnerUniversity} className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
            </span>
          </h3>

          {/* Metadata Row: Location, Est, Type - all in consistent style */}
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs sm:text-sm text-slate-500 font-medium">
            <div className="flex items-center min-w-0">
              <MapPinIcon className="h-4 w-4 mr-1 text-slate-400 shrink-0" />
              <span className="truncate">{location}</span>
            </div>
            {universityType && (
              <>
                <span className="text-slate-300">•</span>
                <span>{universityType}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right Column: Programs Count & Actions */}
      <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-center gap-3 w-full md:w-auto shrink-0 border-t md:border-t-0 pt-3 md:pt-0 border-slate-100">
        {programCount > 0 && (
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500">
            <AcademicCapIcon className="h-4 w-4 text-slate-400 shrink-0" />
            <span>{programCount} Programs Offered</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Link
            to={`/university/${university.slug || university.id}`}
            onClick={(e) => e.stopPropagation()}
            aria-label={`View details for ${name}`}
            className="btn-secondary text-sm px-4 py-2 text-center"
          >
            View Details
          </Link>
        </div>
        {hasApplied && (
          <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/80 px-2 py-0.5 rounded-md text-center">
            Applied • {applicationStatus || 'Submitted'}
          </span>
        )}
      </div>
    </div>
  );
};

export default UniversityCard;
